import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Typography } from "antd";
import { useWatch } from "react-hook-form";
import {
  InputField,
  ReactSelectField,
  InputSwitch,
  DateField,
  TextAreaField,
} from "../../../../../atoms/FormElement";
import {InputNumberFieldV2 as InputNumberField} from "../../../../../atoms/FormElement/InputNumberField";
import {
  dateFormat,
  datePlaceholderddmmyyFormat,
} from "../../../../BookingForm/components/utils";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
var _ = require("lodash");

const { Text } = Typography;

export default ({
  propertyDetails,
  parentId,
  updateApi,
  control,
  setValue,
  errors,
  noEdit,
  watch,
  getValues,
}) => {
  const [block, setBlock] = useState(false);

  const configData = useSelector((state: any) => state.dashboard.configData);
  const unitSubTypes = configData?.usageTypesList?.residential?.apartment;
  const blockWatcher = useWatch({ control, name: "block" });
  const floorNumber = watch("floorNumber");
  const unitNumber = watch("unitNumber");
  const suiteSize = watch("suiteSize");
  const balconySize = watch("balconySize");
  const usageType = watch("usagesType");

  const residentialUnitTypes = ["Apartment", "Villa", "Town House"];
  const commercialUnitTypes = ["Office", "Shop", "Warehouse", "Showroom"];
  // ✅ Prefill values when propertyDetails changes
  useEffect(() => {
    if (propertyDetails?.unit) {
      setValue("floorNumber", propertyDetails.unit.floorNumber || "");
      setValue("unitNumber", propertyDetails.unit.unitNumber || "");
      setValue("unitSubType", propertyDetails.unit.unitSubType || "");
      setValue("areaSqft", propertyDetails.unit.areaSqft || "");
      setValue("referralapprovalcode", propertyDetails.unit.referralapprovalcode || "");
      setValue("balconySize", propertyDetails.unit.balconySize || "");
      setValue("suiteSize", propertyDetails.unit.suiteSize || "");
      if (propertyDetails?.unit?.usagesType) {
        setValue("usagesType", {
          value: propertyDetails.unit.usagesType,
          label: propertyDetails.unit.usagesType,
        });
      }
      if (propertyDetails?.unit?.unitType) {
        setValue("unitType", {
          value: propertyDetails.unit.unitType,
          label: propertyDetails.unit.unitType,
        });
      }
    }

    // root level
    setValue("DMNumber", propertyDetails?.dewaPremiseNumber || "");
    setValue("effectiveStartDate", propertyDetails?.erpActiveStartDate ? dayjs(propertyDetails.erpActiveStartDate) : "");
    setValue("effectiveEndDate", propertyDetails?.erpActiveEndDate ? dayjs(propertyDetails.erpActiveEndDate) : "");
    setValue("spaceDescription", propertyDetails?.description || "");
    setValue("approvedAnnualRent", propertyDetails?.approvedAnnualRent || "");

    // commented ones (still prefilling, but inputs remain commented)
    setValue("propertyAdminName", propertyDetails?.propertyAdminName || "");
    setValue("propertyAdminEmail", propertyDetails?.propertyAdminEmail || "");
  }, [propertyDetails, setValue]);

  useEffect(() => {
    if (!propertyDetails?.unit?.usagesType && !usageType?.value) {
      setValue("unitType", null);
    }
  }, [usageType, propertyDetails, setValue]);

  useEffect(() => {
    if (floorNumber && unitNumber && propertyDetails?.propertyRefNumber) {
      const generatedCode = `${propertyDetails.propertyRefNumber}-${floorNumber}-${unitNumber}`;
      setValue("unitCode", generatedCode);
      return;
    }

    if (propertyDetails?.unit?.unitCode) {
      setValue("unitCode", propertyDetails.unit.unitCode);
      return;
    }

    setValue("unitCode", "");
  }, [propertyDetails?.unit?.unitCode, propertyDetails?.propertyRefNumber, floorNumber, unitNumber, setValue]);

  useEffect(() => {
    const suite = parseFloat(suiteSize) || 0;
    const balcony = parseFloat(balconySize) || 0;

    if (suite || balcony) {
      setValue("areaSqft", suite + balcony);
      return;
    }

    if (propertyDetails?.unit?.areaSqft) {
      setValue("areaSqft", propertyDetails.unit.areaSqft);
      return;
    }

    setValue("areaSqft", "");
  }, [suiteSize, balconySize, propertyDetails?.unit?.areaSqft, setValue]);

  const unitTypeOptions = useMemo(() => {
    if (!usageType?.value) return [];

    return _.map(
      usageType.value === "Residential"
        ? residentialUnitTypes
        : commercialUnitTypes,
      (item) => ({ value: item, label: item })
    );
  }, [usageType]);

  return (
    <>
      <Row gutter={24}>
        <Col span={6}>
          <DateField
            control={control}
            isRequired={true}
            setValue={setValue}
            label="Start Date"
            fieldname="effectiveStartDate"
            validate={errors.effectiveStartDate && "error"}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            rules={{
              required: "Select Start Date",
            }}
            onChange={(date) => {
              if (date) {
                setValue("effectiveStartDate", dayjs(date));
              } else {
                setValue("effectiveStartDate", "");
              }
            }}
            disabled={noEdit}
            iProps={{
              readOnly: noEdit,
              format: datePlaceholderddmmyyFormat,
              placeholder: datePlaceholderddmmyyFormat,
              disabledDate: (date) => {
                const endDate = getValues("effectiveEndDate");
                return endDate ? date.isAfter(dayjs(endDate), "day") : false;
              },
            }}
            valueGot={dateFormat(null)}
            validMessage={
              errors.effectiveStartDate && errors.effectiveStartDate.message
            }
          />
        </Col>

        <Col span={6}>
          <DateField
            control={control}
            isRequired={true}
            setValue={setValue}
            label="End Date"
            fieldname="effectiveEndDate"
            validate={errors.effectiveStartDate && "error"}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            rules={{
              required: "Select End Date",
            }}
            onChange={(date) => {
              if (date) {
                setValue("effectiveEndDate", dayjs(date));
              } else {
                setValue("effectiveEndDate", "");
              }
            }}
            disabled={noEdit}
            iProps={{
              readOnly: noEdit,
              format: datePlaceholderddmmyyFormat,
              placeholder: datePlaceholderddmmyyFormat,
              disabledDate: (date) => {
                const startDate = getValues("effectiveStartDate");
                return startDate
                  ? date.isBefore(dayjs(startDate), "day")
                  : false;
              },
            }}
            valueGot={dateFormat(null)}
            validMessage={
              errors.effectiveEndDate && errors.effectiveEndDate.message
            }
          />
        </Col>

        <Col span={6}>
          <ReactSelectField
            isRequired={true}
            label="Usages Type"
            fieldname="usagesType"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            iProps={{
              placeholder: "Select Usages Type",
              isDisabled: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            selectOption={["Residential", "Commercial"].map((item) => ({
              value: item,
              label: item,
            }))}
            rules={{ required: "Select Usages Type" }}
            validate={errors.usagesType && "error"}
            validMessage={errors.usagesType && errors.usagesType.message}
          />
        </Col>

        <Col span={6}>
          <ReactSelectField
            isRequired={true}
            label="Unit Type"
            fieldname="unitType"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            iProps={{
              placeholder: !usageType?.value
                ? "Select Usages Type First"
                : "Select Unit Type",
              isDisabled: !usageType?.value || noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            selectOption={unitTypeOptions}
            rules={{ required: "Select Unit Type" }}
            validate={errors.unitType && "error"}
            validMessage={errors.unitType && errors.unitType.message}
          />
        </Col>

        <Col span={6}>
          <ReactSelectField
            isRequired={true}
            label="Unit Sub Type"
            fieldname="unitSubType"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Select one",
              isDisabled: noEdit,
              className: `${noEdit ? "disablerselect" : ""}`,
            }}
            selectOption={Array.from({ length: 8 }, (_, i) => {
              const label = `${i + 1}BR`;
              return { value: label, label };
            })}
            rules={{ required: "Enter Unit Sub Type" }}
            validate={errors.unitSubType && "error"}
            validMessage={errors.unitSubType && errors.unitSubType.message}
          />
        </Col>
        
        <Col span={6}>
          <InputField
            isRequired={true}
            label="Floor Number"
            fieldname="floorNumber"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Floor Number",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            rules={{ required: "Enter Floor Number" }}
            validate={errors.floorNumber && "error"}
            validMessage={errors.floorNumber && errors.floorNumber.message}
          />
        </Col>
        <Col span={6}>
          <InputField
            isRequired={true}
            label="Unit Number"
            fieldname="unitNumber"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Unit Number",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            rules={{ required: "Enter Unit Number" }}
            validate={errors.unitNumber && "error"}
            validMessage={errors.unitNumber && errors.unitNumber.message}
          />
        </Col>
        <Col span={6}>
          <InputField
            isRequired={true}
            label="Unit Code"
            fieldname="unitCode"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Prop Ref No - Fl No - Un No.",
              readOnly: true,
              className: "noEdit",
            }}
            rules={{ required: "Enter Floor and Unit Number" }}
            validate={errors.unitCode && "error"}
            validMessage={errors.unitCode && errors.unitCode.message}
          />
        </Col>

        <Col span={6}>
          <InputNumberField
            isRequired
            label="Internal Area Sq.ft"
            fieldname="suiteSize"
            control={control}
            inputNumberProps={{
              placeholder: "Enter Suite Size",
              precision: 2,
            }}
            disabled={noEdit}
            rules={{ required: "Enter Suite Size" }}
          />
        </Col>

        <Col span={6}>
          <InputNumberField
            isRequired
            label="External Area Sq.ft"
            fieldname="balconySize"
            control={control}
            inputNumberProps={{
              placeholder: "Enter Balcony Size",
              precision: 2,
            }}
            disabled={noEdit}
            rules={{ required: "Enter Balcony Size" }}
          />
        </Col>

        <Col span={6}>
          <InputNumberField
            isRequired
            label="Total Area Sq.ft"
            fieldname="areaSqft"
            control={control}
            inputNumberProps={{ precision: 2 }}
            disabled
            rules={{
              required: "Please enter Size",
            }}
          />
        </Col>

        <Col span={6}>
          <InputField
            isRequired={true}
            label="Dewa Premise Number"
            fieldname="DMNumber"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Dewa Premise Number",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            rules={{ required: "Enter Dewa Premise Number" }}
            validate={errors.DMNumber && "error"}
            validMessage={errors.DMNumber && errors.DMNumber.message}
          />
        </Col>

        <Col span={6}>
          <InputField
            isRequired={true}
            label="Referral Approval Code"
            fieldname="referralapprovalcode"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Enter Referral Approval Code",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            rules={{ required: "Enter Referral Approval Code" }}
            validate={errors.referralapprovalcode && "error"}
            validMessage={
              errors.referralapprovalcode && errors.referralapprovalcode.message
            }
          />
        </Col>

        <Col span={6}>
          <InputNumberField
            isRequired
            label="Approved Annual Rent"
            fieldname="approvedAnnualRent"
            control={control}
            inputNumberProps={{
              placeholder: "Approved Annual Rent",
              precision: 2,
            }}
            disabled={noEdit}
          />
        </Col>

        <Col span={6}>
          <InputField
            isRequired={false}
            label="Property Admin Name"
            fieldname="propertyAdminName"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Enter Property Admin Name",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            disabled={propertyDetails?.propertyAdminName}
          />
        </Col>

        <Col span={6}>
          <InputField
            isRequired={false}
            label="Property Admin Email"
            fieldname="propertyAdminEmail"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Enter Property Admin Email",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
            disabled={propertyDetails?.propertyAdminEmail}
          />
        </Col>

        <Col span={6}>
          <TextAreaField
            isRequired={false}
            label="Space Description"
            fieldname="spaceDescription"
            control={control}
            classes={`${noEdit ? "noEditLabel" : ""}`}
            initValue=""
            iProps={{
              placeholder: "Space Description",
              readOnly: noEdit,
              className: `${noEdit ? "noEdit" : ""}`,
            }}
          />
        </Col>
      </Row>

      {propertyDetails.unitStatus === "vacant" && (
        <Row gutter={24}>
          <Col span={6}>
            <Text
              className={`${noEdit ? "noEditLabel" : ""}`}
              style={
                noEdit
                  ? { fontWeight: 500, color: "#767676", fontSize: 14 }
                  : { fontSize: 14 }
              }
            >
              Block Unit
            </Text>
            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <InputSwitch
                label="Blocking Reason"
                fieldname={`block`}
                iProps={{
                  disabled: noEdit,
                }}
                control={control}
                valueGot={block}
                setValue={setValue}
              />
            </div>
          </Col>
          {blockWatcher && (
            <Col span={6}>
              <InputField
                isRequired={!noEdit}
                label="Blocking Reason"
                fieldname="blockreason"
                control={control}
                classes={`${noEdit ? "noEditLabel" : ""}`}
                initValue=""
                rules={{
                  required: "Please enter Blocking Reason",
                }}
                iProps={{
                  placeholder: "Blocking Reason",
                  readOnly: noEdit,
                  className: `${noEdit ? "noEdit" : ""}`,
                }}
                validate={errors.blockreason && "error"}
                validMessage={errors.blockreason && errors.blockreason.message}
              />
            </Col>
          )}

          {propertyDetails?.blockData?.name && blockWatcher && (
            <Col span={6}>
              <InputField
                isRequired={false}
                label="Blocked By"
                fieldname="blockedBy"
                initValue={`${propertyDetails?.blockData?.name} on ${dayjs(
                  propertyDetails?.blockData?.createdAt
                ).format("D MMMM YYYY")}`}
                control={control}
                classes={`${noEdit ? "noEditLabel" : ""}`}
                iProps={{
                  readOnly: true,
                  className: `noEdit`,
                }}
              />
            </Col>
          )}
        </Row>
      )}
    </>
  );
};
